import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus, MessageCircle } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { FAQS, waLink } from "@/data/site";

const FaqItem = ({ item, index }) => {
  const [open, setOpen] = useState(index === 0);
  return (
    <Reveal delay={index * 0.05}>
      <div className="border-b border-bronze/10">
        <button
          data-testid={`faq-question-${index + 1}`}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="w-full flex items-center justify-between gap-6 py-7 text-left group"
        >
          <span
            className={`font-serif text-xl sm:text-2xl transition-colors duration-300 ${
              open ? "text-bronze" : "text-ink group-hover:text-bronze"
            }`}
          >
            {item.q}
          </span>
          <motion.span
            animate={{ rotate: open ? 45 : 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className={`shrink-0 rounded-full border p-2.5 transition-colors duration-300 ${
              open ? "border-bronze bg-bronze text-cream" : "border-bronze/25 text-bronze"
            }`}
          >
            <Plus size={15} />
          </motion.span>
        </button>
        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden"
            >
              <p data-testid={`faq-answer-${index + 1}`} className="pb-8 pr-10 text-sm sm:text-base leading-relaxed text-stone2 max-w-3xl">
                {item.a}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Reveal>
  );
};

export default function Faq() {
  return (
    <>
      <Seo
        title="Spa FAQs — Booking, Pricing, Hygiene & Couple Suites | Ody Manikonda"
        description="Spa FAQs for Ody Luxurious Spa Manikonda: how to book a massage, session durations, therapist preferences, hygiene standards, couple suites and location directions."
        keywords="spa booking Hyderabad, massage FAQ, couple spa Manikonda, spa hygiene, spa near me Manikonda"
      />
      <PageHero
        eyebrow="Good Questions"
        title="Everything guests usually ask"
        subtitle="Booking, hygiene, therapist preferences, couple services and more. Anything missing — we are one WhatsApp message away."
        crumbs={[{ label: "FAQ" }]}
      />
      <section className="py-16 sm:py-24">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          {FAQS.map((f, i) => (
            <FaqItem key={f.q} item={f} index={i} />
          ))}
          <Reveal delay={0.2}>
            <div className="mt-14 rounded-3xl bg-alabaster border border-bronze/10 p-8 sm:p-10 text-center">
              <h2 className="font-serif text-2xl text-ink">Still wondering about something?</h2>
              <p className="mt-3 text-sm text-stone2">
                Ask us directly — replies are quick, and there are no silly questions.
              </p>
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="faq-whatsapp-btn"
                className="mt-7 inline-flex items-center gap-3 rounded-full bg-wa text-white px-8 py-4 text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-wa-dark"
              >
                <MessageCircle size={15} /> Ask on WhatsApp
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
